import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, router } from '@inertiajs/react'
import { getDateLocale } from '@/lib/i18n'
import { AdminLayout } from '@/layouts/admin-layout'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { GlobeIcon, ExternalLinkIcon } from 'lucide-react'

interface Project {
  id: number
  name: string
  archived: boolean
}

interface Organization {
  id: number
  name: string
}

interface Domain {
  id: number
  domain_name: string
  registration_type: 'purchased' | 'byod'
  purchase_status: 'pending' | 'processing' | 'completed' | 'failed' | 'expired'
  activation_status: 'not_activated' | 'configuring_dns' | 'activating_certificate' | 'active' | 'failed' | 'stale'
  cost_credits: number | null
  is_apex_domain: boolean
  cname_target: string | null
  expires_at: string | null
  activated_at: string | null
  created_at: string
  project: Project
  organization: Organization | null
}

interface Pagination {
  page: number
  pages: number
  count: number
  from: number
  to: number
}

interface Filters {
  activation_status?: string
  registration_type?: string
  purchase_status?: string
  search?: string
}

interface DomainsIndexProps {
  domains: Domain[]
  pagy: Pagination
  filters: Filters
}

const ACTIVATION_BADGE_CONFIG: Record<Domain['activation_status'], { variant: 'default' | 'secondary' | 'destructive' | 'outline'; className: string }> = {
  active: { variant: 'default', className: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700' },
  not_activated: { variant: 'outline', className: 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-700' },
  configuring_dns: { variant: 'outline', className: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700' },
  activating_certificate: { variant: 'outline', className: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700' },
  failed: { variant: 'destructive', className: '' },
  stale: { variant: 'destructive', className: 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-700' },
}

const PURCHASE_BADGE_CONFIG: Record<Domain['purchase_status'], { variant: 'default' | 'secondary' | 'destructive' | 'outline'; className: string }> = {
  completed: { variant: 'default', className: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700' },
  pending: { variant: 'outline', className: 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-700' },
  processing: { variant: 'outline', className: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700' },
  failed: { variant: 'destructive', className: '' },
  expired: { variant: 'secondary', className: '' },
}

function ActivationBadge({ status }: { status: Domain['activation_status'] }) {
  const { t } = useTranslation('admin')
  const c = ACTIVATION_BADGE_CONFIG[status]
  return (
    <Badge variant={c.variant} className={c.className}>
      {t(`domains.badges.${status}`)}
    </Badge>
  )
}

function PurchaseBadge({ status }: { status: Domain['purchase_status'] }) {
  const { t } = useTranslation('admin')
  const c = PURCHASE_BADGE_CONFIG[status]
  return (
    <Badge variant={c.variant} className={c.className}>
      {t(`domains.badges.${status}`)}
    </Badge>
  )
}

function Index({ domains, pagy, filters }: DomainsIndexProps) {
  const { t } = useTranslation('admin')
  const [search, setSearch] = useState(filters.search || '')
  const [activationStatus, setActivationStatus] = useState(filters.activation_status || 'all')
  const [registrationType, setRegistrationType] = useState(filters.registration_type || 'all')
  const [purchaseStatus, setPurchaseStatus] = useState(filters.purchase_status || 'all')
  const [releaseTarget, setReleaseTarget] = useState<Domain | null>(null)

  const buildParams = () => {
    const params: Record<string, string> = {}
    if (search) params.search = search
    if (activationStatus !== 'all') params.activation_status = activationStatus
    if (registrationType !== 'all') params.registration_type = registrationType
    if (purchaseStatus !== 'all') params.purchase_status = purchaseStatus
    return params
  }

  const handleSearch = () => {
    router.get('/admin/domains', buildParams(), { preserveState: true })
  }

  const handleReset = () => {
    setSearch('')
    setActivationStatus('all')
    setRegistrationType('all')
    setPurchaseStatus('all')
    router.get('/admin/domains', {}, { preserveState: true })
  }

  const handleRelease = () => {
    if (!releaseTarget) return
    router.delete(`/admin/domains/${releaseTarget.id}`, {
      onFinish: () => setReleaseTarget(null),
    })
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col gap-2 py-2">
          <div className="px-4 lg:px-6">
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <GlobeIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-foreground">
                    {t('domains.title')}
                  </h1>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t('domains.description', { count: pagy.count })}
                  </p>
                </div>
              </div>
            </div>

            {/* Filters */}
            <Card className="shadow-none mb-6 p-3">
              <div className="flex items-end justify-between flex-wrap gap-3">
                <div className="flex items-end flex-wrap gap-3 flex-1">
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-xs font-medium text-foreground block mb-1">
                      {t('domains.search')}
                    </label>
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={t('domains.search')}
                      className="h-9 text-sm"
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    />
                  </div>
                  <div className="flex-1 min-w-[160px]">
                    <label className="text-xs font-medium text-foreground block mb-1">
                      {t('domains.filters.activationStatus')}
                    </label>
                    <Select value={activationStatus} onValueChange={setActivationStatus}>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue placeholder={t('domains.filters.all')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('domains.filters.all')}</SelectItem>
                        <SelectItem value="active">{t('domains.badges.active')}</SelectItem>
                        <SelectItem value="not_activated">{t('domains.badges.not_activated')}</SelectItem>
                        <SelectItem value="configuring_dns">{t('domains.badges.configuring_dns')}</SelectItem>
                        <SelectItem value="activating_certificate">{t('domains.badges.activating_certificate')}</SelectItem>
                        <SelectItem value="failed">{t('domains.badges.failed')}</SelectItem>
                        <SelectItem value="stale">{t('domains.badges.stale')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 min-w-[140px]">
                    <label className="text-xs font-medium text-foreground block mb-1">
                      {t('domains.filters.registrationType')}
                    </label>
                    <Select value={registrationType} onValueChange={setRegistrationType}>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue placeholder={t('domains.filters.all')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('domains.filters.all')}</SelectItem>
                        <SelectItem value="purchased">{t('domains.badges.purchased')}</SelectItem>
                        <SelectItem value="byod">{t('domains.badges.byod')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 min-w-[160px]">
                    <label className="text-xs font-medium text-foreground block mb-1">
                      {t('domains.filters.purchaseStatus')}
                    </label>
                    <Select value={purchaseStatus} onValueChange={setPurchaseStatus}>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue placeholder={t('domains.filters.all')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('domains.filters.all')}</SelectItem>
                        <SelectItem value="pending">{t('domains.badges.pending')}</SelectItem>
                        <SelectItem value="processing">{t('domains.badges.processing')}</SelectItem>
                        <SelectItem value="completed">{t('domains.badges.completed')}</SelectItem>
                        <SelectItem value="failed">{t('domains.badges.failed')}</SelectItem>
                        <SelectItem value="expired">{t('domains.badges.expired')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  <Button onClick={handleSearch} className="h-9 px-4 text-sm">
                    {t('domains.buttons.search')}
                  </Button>
                  <Button onClick={handleReset} variant="outline" className="h-9 px-4 text-sm">
                    {t('domains.buttons.reset')}
                  </Button>
                </div>
              </div>
            </Card>

            {/* Table */}
            <div className="bg-card rounded-lg border border-border overflow-hidden">
              <div className="overflow-auto" style={{ maxHeight: '600px' }}>
                <table className="w-full">
                  <thead className="bg-muted sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('domains.columns.domainName')}
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('domains.columns.project')}
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('domains.columns.registrationType')}
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('domains.columns.purchaseStatus')}
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('domains.columns.activationStatus')}
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('domains.columns.createdAt')}
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                    {domains.length > 0 ? (
                      domains.map((domain) => (
                        <tr key={domain.id} className="hover:bg-muted transition-colors">
                          <td className="px-3 py-3 whitespace-nowrap">
                            <Link
                              href={`/admin/domains/${domain.id}`}
                              className="text-sm font-medium text-foreground hover:text-primary"
                            >
                              {domain.domain_name}
                            </Link>
                            {domain.cname_target && (
                              <div className="text-xs text-muted-foreground mt-0.5">
                                CNAME: {domain.cname_target}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <Link
                                href={`/admin/projects/${domain.project.id}`}
                                className="text-sm text-foreground hover:text-primary flex items-center gap-1"
                              >
                                {domain.project.name}
                                <ExternalLinkIcon className="h-3 w-3" />
                              </Link>
                              {domain.project.archived && (
                                <Badge variant="outline" className="text-xs ml-1">
                                  {t('domains.archived')}
                                </Badge>
                              )}
                            </div>
                            {domain.organization && (
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {domain.organization.name}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <Badge variant={domain.registration_type === 'purchased' ? 'default' : 'secondary'}>
                              {t(`domains.badges.${domain.registration_type}`)}
                            </Badge>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <PurchaseBadge status={domain.purchase_status} />
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <ActivationBadge status={domain.activation_status} />
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <div className="text-sm text-foreground">
                              {new Date(domain.created_at).toLocaleDateString(getDateLocale(), {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-right">
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => setReleaseTarget(domain)}
                            >
                              {t('domains.release.button')}
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="px-6 py-12 text-center text-muted-foreground">
                          {t('domains.empty')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagy.pages > 1 && (
                <div className="px-6 py-4 flex items-center justify-between border-t border-border bg-card">
                  <div className="text-sm text-foreground">
                    {t('domains.pagination.showing', {
                      from: pagy.from,
                      to: pagy.to,
                      count: pagy.count,
                    })}
                  </div>
                  <div className="flex gap-2">
                    {pagy.page > 1 && (
                      <Button
                        variant="outline"
                        onClick={() => router.get('/admin/domains', { ...buildParams(), page: pagy.page - 1 }, { preserveState: true })}
                      >
                        {t('domains.pagination.previous')}
                      </Button>
                    )}
                    {pagy.page < pagy.pages && (
                      <Button
                        variant="outline"
                        onClick={() => router.get('/admin/domains', { ...buildParams(), page: pagy.page + 1 }, { preserveState: true })}
                      >
                        {t('domains.pagination.next')}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Release Confirmation Dialog */}
      <AlertDialog open={!!releaseTarget} onOpenChange={(open) => !open && setReleaseTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('domains.release.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {releaseTarget && t('domains.release.description', {
                domain: releaseTarget.domain_name,
                project: releaseTarget.project.name,
              })}
              {releaseTarget?.registration_type === 'purchased' && (
                <span className="block mt-2 font-semibold text-destructive">
                  {t('domains.release.purchasedWarning')}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('domains.release.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleRelease}
            >
              {t('domains.release.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

Index.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Index
